import { Controller } from "@hotwired/stimulus"

// Handles document uploads in the Claims Coach chat.
// When a file is selected, the multipart turbo_stream form is auto-submitted
// via requestSubmit(). The "Uploading..." spinner is shown on submit-start and
// hidden on submit-end, at which point the file input is reset so the same file
// (or a new one) can be picked again. On submit-end a result row acknowledges
// success or failure (Turbo sets event.detail.success false for non-2xx
// responses and network failures), auto-hiding after a few seconds.
export default class extends Controller {
  static targets = ["input", "form", "category", "uploading", "result"]

  // classes applied to the result row per outcome (warm-clay tokens; mirrors
  // the moss/ember convention used in coach/_error.html.erb)
  static SUCCESS_CLASS = "text-moss-600"
  static ERROR_CLASS = "text-ember-700"
  static AUTO_HIDE_MS = 5000

  disconnect() {
    this.clearResultTimeout()
  }

  fileSelected() {
    if (!this.hasInputTarget || !this.hasFormTarget) return
    if (!this.inputTarget.files || this.inputTarget.files.length === 0) return
    // RL-144b: double-submit guard — ignore a second selection while an upload
    // is already in flight so a fast re-pick can't create two Document rows.
    if (this.submitting) return

    this.submitting = true
    // The form is a turbo_stream form; requestSubmit() lets Turbo intercept
    // and handle the response (and CSRF) the same way a manual submit would.
    this.formTarget.requestSubmit()
  }

  formSubmitStart() {
    this.submitting = true
    // clear any stale result from a previous upload before the new one runs
    this.hideResult()
    if (this.hasUploadingTarget) {
      this.uploadingTarget.classList.remove("hidden")
    }
  }

  formSubmitEnd(event) {
    this.submitting = false
    if (this.hasUploadingTarget) {
      this.uploadingTarget.classList.add("hidden")
    }
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
    }

    const success = event && event.detail ? event.detail.success : true
    if (success) {
      this.showResult("Uploaded — analysis will appear in the chat", this.constructor.SUCCESS_CLASS)
    } else {
      this.showResult("Upload failed — please try again", this.constructor.ERROR_CLASS)
    }
  }

  showResult(message, className) {
    if (!this.hasResultTarget) return
    this.clearResultTimeout()
    this.resultTarget.textContent = message
    this.resultTarget.classList.remove("hidden", this.constructor.SUCCESS_CLASS, this.constructor.ERROR_CLASS)
    this.resultTarget.classList.add(className)
    this.resultTimeout = setTimeout(() => this.hideResult(), this.constructor.AUTO_HIDE_MS)
  }

  hideResult() {
    this.clearResultTimeout()
    if (!this.hasResultTarget) return
    this.resultTarget.textContent = ""
    this.resultTarget.classList.add("hidden")
    this.resultTarget.classList.remove(this.constructor.SUCCESS_CLASS, this.constructor.ERROR_CLASS)
  }

  clearResultTimeout() {
    if (this.resultTimeout) {
      clearTimeout(this.resultTimeout)
      this.resultTimeout = null
    }
  }
}
